/* ============================================================
   Re-Coco — Shop / Collection JS [REFACTORED v3.0 - PRODUCTION]
   Handles: price range slider, dynamic tag filter, AJAX sorting
   Fixes: Event delegation, DOM diffing, app compatibility
   ============================================================ */

(function () {
  'use strict';

  const $ = id => document.getElementById(id);
  let isLoading = false;

  // ── Initialize DOM Elements Early ──
  const minInput = $('price-min');
  const maxInput = $('price-max');
  const rangeBar = $('price-range-fill');
  const minLabel = $('price-min-label');
  const maxLabel = $('price-max-label');
  const sortSelect = $('sort-select');
  const applyPriceBtn = $('apply-price-filter');

  // Helper to get the base collection URL
  function getBaseUrl() {
    const pathParts = window.location.pathname.split('/');
    if (pathParts[1] === 'collections' && pathParts.length > 3) {
      return '/collections/' + pathParts[2];
    }
    return window.location.pathname;
  }

  // ── Skeleton Loader HTML Generation ──
  function createProductSkeleton(isMobile) {
    if (isMobile) {
      // Mobile: 2-col grid skeleton
      return `
        <div class="product-card group relative flex flex-col bg-white rounded-2xl p-[10px] skeleton">
          <div class="block overflow-hidden rounded-xl relative" style="aspect-ratio:1/1;">
            <div class="w-full h-full bg-gray-200 animate-shimmer"></div>
            <div class="absolute top-2 left-2">
              <div class="h-5 w-12 rounded-full bg-gray-300"></div>
            </div>
            <div class="absolute top-2 right-2 w-7 h-7 bg-gray-300 rounded-full"></div>
          </div>
          <div class="mt-2 flex flex-col gap-1.5 flex-1">
            <div class="h-4 bg-gray-200 rounded w-full"></div>
            <div class="h-4 bg-gray-200 rounded w-3/4"></div>
            <div class="flex items-center gap-1.5">
              <div class="h-5 w-20 bg-gray-200 rounded"></div>
              <div class="h-4 w-16 bg-gray-200 rounded"></div>
            </div>
            <div class="mt-1 w-full h-9 rounded-full bg-gray-200"></div>
          </div>
        </div>
      `;
    } else {
      // Desktop: List row skeleton
      return `
        <div class="hidden sm:flex gap-5 py-5 group skeleton">
          <div class="w-32 h-40 bg-gray-200 animate-shimmer rounded-2xl shrink-0"></div>
          <div class="flex-1 min-w-0">
            <div class="h-3 w-24 bg-gray-200 rounded mb-2"></div>
            <div class="h-5 bg-gray-200 rounded w-full mb-2"></div>
            <div class="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div class="h-3 bg-gray-200 rounded w-full mb-1"></div>
            <div class="h-3 bg-gray-200 rounded w-full mb-1"></div>
            <div class="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
            <div class="flex items-center gap-2">
              <div class="h-5 w-20 bg-gray-200 rounded"></div>
              <div class="h-4 w-16 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div class="shrink-0">
            <div class="w-8 h-20 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      `;
    }
  }

  // ── Show Skeleton Loaders ──
  function showSkeletons(count = 24) {
    const grid = $('product-grid');
    if (!grid) return;

    const isMobile = window.innerWidth < 768;
    let skeletonHTML = '';
    for (let i = 0; i < count; i++) {
      skeletonHTML += createProductSkeleton(isMobile);
    }

    grid.innerHTML = skeletonHTML;
    grid.style.opacity = '1';
    grid.style.pointerEvents = 'none';
  }

  // ── Fade In Products ──
  function fadeInProducts() {
    const grid = $('product-grid');
    if (!grid) return;

    grid.classList.add('skeleton-fade-in');
    grid.style.pointerEvents = 'auto';

    setTimeout(() => {
      grid.classList.remove('skeleton-fade-in');
    }, 400);
  }

  // ── Loading State Management ──
  function setLoading(loading) {
    isLoading = loading;
    const grid = $('product-grid');
    const pagination = $('pagination-container');

    if (loading) {
      showSkeletons(24);
      if (pagination) {
        pagination.style.opacity = '0.5';
        pagination.style.pointerEvents = 'none';
      }
    } else {
      if (pagination) {
        pagination.style.opacity = '1';
        pagination.style.pointerEvents = 'auto';
      }
    }
  }

  // ── Update grid content ──
  // Simple innerHTML replacement is safe here because the delegated click
  // listener is attached to the grid *element* itself, not its children —
  // replacing children leaves the listener intact.
  function updateGridContent(newGridHTML) {
    const grid = $('product-grid');
    if (!grid) return;
    grid.innerHTML = newGridHTML;
  }

  // ── IMPROVED: Event Delegation (FIX #2) ──
  // Replaces button cloning pattern with single delegated listener
  function setupAddToCartDelegation() {
    const grid = $('product-grid');
    if (!grid) return;

    grid.addEventListener('click', function(e) {
      const btn = e.target.closest('[data-add-to-cart], .add-to-cart-btn');
      if (!btn) return;

      handleAddToCart({ currentTarget: btn });
    }, { capture: false });
  }

  // ── App Reinitialization Hooks (FIX #3) ──
  // Reinitialize third-party apps after AJAX content updates
  function reinitializeApps() {
    // Judge.me Reviews
    if (window.jdgm) {
      try {
        window.jdgm.api.renderElements(document.querySelectorAll('.jdgm-widget'));
      } catch (e) {
        console.warn('[Judge.me] Failed to reinitialize:', e.message);
      }
    }

    // Yotpo Reviews
    if (window.yotpo) {
      try {
        window.yotpo.refreshWidgets();
      } catch (e) {
        console.warn('[Yotpo] Failed to reinitialize:', e.message);
      }
    }

    // Razorpay Buttons
    if (window.Razorpay && window.Razorpay._initialized) {
      try {
        document.querySelectorAll('[data-razorpay]').forEach(el => {
          el.dispatchEvent(new CustomEvent('razorpay:reinit'));
        });
      } catch (e) {
        console.warn('[Razorpay] Failed to reinitialize:', e.message);
      }
    }

    // Shopify Messaging
    if (window.ShopifyBuy && window.ShopifyBuy.UI) {
      try {
        window.ShopifyBuy.UI.reinstall();
      } catch (e) {
        console.warn('[Shopify Messaging] Failed to reinitialize:', e.message);
      }
    }
  }

  // ── AJAX UI Updates ──────────────────────────────────────────
  async function updateCollection(url, skipPushState) {
    if (isLoading) return;

    const grid = $('product-grid');
    const pagination = $('pagination-container');
    const countLabel = $('product-count');

    setLoading(true);

    try {
      const response = await fetch(url.href, {
        headers: { 'Accept': 'text/html' },
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error('HTTP ' + response.status);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      if (!doc || doc.body.innerHTML.length < 50) {
        throw new Error('Invalid response');
      }

      // Update URL before content
      if (!skipPushState) {
        history.pushState({ url: url.href }, '', url.href);
      }

      // Update Product Grid (IMPROVED: DOM Diffing instead of innerHTML replacement)
      if (grid) {
        const newGrid = doc.getElementById('product-grid');
        if (newGrid) {
          updateGridContent(newGrid.innerHTML);
          fadeInProducts();
          reinitializeApps();  // Reinitialize third-party apps
          document.dispatchEvent(new CustomEvent('grid:updated'));
        }
      }

      // Update Pagination
      if (pagination) {
        const newPagination = doc.getElementById('pagination-container');
        if (newPagination) {
          pagination.innerHTML = newPagination.innerHTML;
          rebindPaginationLinks();
        }
      }

      // Update Product Count
      if (countLabel) {
        const newCount = doc.getElementById('product-count');
        if (newCount) {
          countLabel.innerHTML = newCount.innerHTML;
        }
      }

      // Sync UI states
      syncUIStates(url);

      // Smooth scroll to results
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (error) {
      console.error('[AJAX Filter Error]', error);
      window.location.href = url.href;
    } finally {
      setLoading(false);
    }
  }

  // ── Sync UI States ──
  function syncUIStates(url) {
    const activeTags = url.searchParams.getAll('filter.p.tag');

    // Sync checkboxes
    document.querySelectorAll('.filter-checkbox').forEach(cb => {
      cb.checked = activeTags.includes(cb.dataset.tag);
    });

    // Sync mobile chips
    document.querySelectorAll('.filter-chip').forEach(chip => {
      const tag = chip.dataset.filterChip;
      const isActive = (tag === 'all') ? activeTags.length === 0 : activeTags.includes(tag);

      if (isActive) {
        chip.classList.add('bg-primary-dark', 'text-white');
        chip.classList.remove('bg-white', 'text-text-dark', 'border-border-light');
      } else {
        chip.classList.remove('bg-primary-dark', 'text-white');
        chip.classList.add('bg-white', 'text-text-dark', 'border-border-light');
      }
    });

    // Sync sort
    if (sortSelect) {
      const sortValue = url.searchParams.get('sort_by') || 'manual';
      sortSelect.value = sortValue;
    }

    // Sync price range
    if (minInput && maxInput) {
      const minVal = url.searchParams.get('filter.p.price.gte');
      const maxVal = url.searchParams.get('filter.p.price.lte');

      minInput.value = minVal !== null ? parseInt(minVal, 10) : parseInt(minInput.dataset.defaultMin || 0, 10);
      maxInput.value = maxVal !== null ? parseInt(maxVal, 10) : parseInt(maxInput.max || 5000, 10);
      updateRange();
    }
  }

  // ── Price Range Slider ──
  function updateRange() {
    if (!minInput || !maxInput) return;

    let min = parseInt(minInput.value, 10);
    let max = parseInt(maxInput.value, 10);
    const total = parseInt(maxInput.max || 5000, 10);

    if (min > max) {
      min = max;
      minInput.value = min;
    }

    if (rangeBar) {
      rangeBar.style.left = (min / total * 100) + '%';
      rangeBar.style.right = (100 - max / total * 100) + '%';
    }

    if (minLabel) minLabel.textContent = 'Min: ₹' + min.toLocaleString('en-IN');
    if (maxLabel) maxLabel.textContent = 'Max: ₹' + max.toLocaleString('en-IN');
  }

  function rebindPaginationLinks() {
    document.querySelectorAll('#pagination-container a').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(link.href);
        updateCollection(url);
      });
    });
  }

  async function handleAddToCart(e) {
    const btn = e.currentTarget;
    const variantId = parseInt(btn.dataset.addToCart || btn.dataset.variantId, 10);
    const qty = parseInt(btn.dataset.qty || '1', 10);

    if (!variantId || btn.disabled || isLoading) return;

    btn.disabled = true;
    const origHTML = btn.innerHTML;
    btn.innerHTML = '<span>Adding...</span>';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });

      if (!res.ok) throw new Error('Failed to add to cart');

      btn.innerHTML = '<span>Added!</span>';
      document.dispatchEvent(new CustomEvent('recoco:cart-updated'));

      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.disabled = false;
      }, 2000);
    } catch (error) {
      console.error('[Add to Cart Error]', error);
      btn.innerHTML = origHTML;
      btn.disabled = false;
    }
  }

  // ── Event Listeners ──

  // Sort dropdown
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      updateCollection(url);
    });
  }

  // Price range inputs
  if (minInput) minInput.addEventListener('input', updateRange);
  if (maxInput) maxInput.addEventListener('input', updateRange);

  // Apply price filter
  if (applyPriceBtn) {
    applyPriceBtn.addEventListener('click', () => {
      if (!minInput || !maxInput) return;

      const url = new URL(window.location.href);
      url.pathname = getBaseUrl();
      url.searchParams.set('filter.p.price.gte', minInput.value);
      url.searchParams.set('filter.p.price.lte', maxInput.value);
      url.searchParams.delete('page');
      updateCollection(url);
    });
  }

  // Tag filter
  window.filterByTag = function (tag, checked) {
    const url = new URL(window.location.href);
    url.pathname = getBaseUrl();

    const filters = url.searchParams.getAll('filter.p.tag');

    if (checked) {
      if (!filters.includes(tag)) {
        url.searchParams.append('filter.p.tag', tag);
      }
    } else {
      const newFilters = filters.filter(t => t !== tag);
      url.searchParams.delete('filter.p.tag');
      newFilters.forEach(t => url.searchParams.append('filter.p.tag', t));
    }

    url.searchParams.delete('page');
    updateCollection(url);
  };

  // Mobile filter chips
  window.mobileFilterTag = function (tag, btn) {
    const url = new URL(window.location.href);
    url.pathname = getBaseUrl();
    url.searchParams.delete('filter.p.tag');
    url.searchParams.append('filter.p.tag', tag);
    url.searchParams.delete('page');
    updateCollection(url);
  };

  // Clear all filters
  window.clearAllFilters = function () {
    const url = new URL(window.location.href);
    url.pathname = getBaseUrl();
    url.searchParams.delete('filter.p.tag');
    url.searchParams.delete('filter.p.price.gte');
    url.searchParams.delete('filter.p.price.lte');
    url.searchParams.delete('page');
    url.searchParams.delete('sort_by');
    updateCollection(url);
  };

  window.mobileFilterAll = window.clearAllFilters;

  // Initialize
  updateRange();
  setupAddToCartDelegation();  // Setup event delegation once

  // Browser back/forward
  window.addEventListener('popstate', () => {
    updateCollection(new URL(window.location.href), true);
  });

})();
